import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast, ToastContainer } from 'react-toastify';
import { auth } from '../firebaseConfig';
import RegistrationProgress from '../components/RegistrationProgress';
import {
  clearCurrentUserProfile,
  loadUserProfile,
  persistCurrentUserProfile,
} from '../utils/authVerification';

export default function VerifyReview() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);

  const refreshStatus = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) {
      clearCurrentUserProfile();
      navigate('/login', { replace: true });
      return;
    }

    await user.reload();
    if (!user.emailVerified) {
      localStorage.setItem('pendingEmail', user.email || '');
      navigate('/verify-email', { replace: true });
      return;
    }

    const profileData = await loadUserProfile(user.uid);
    if (!profileData) {
      clearCurrentUserProfile();
      toast.error('User profile not found. Please sign in again.');
      navigate('/login', { replace: true });
      return;
    }

    persistCurrentUserProfile(user.uid, profileData);

    if (profileData.phoneVerified !== true) {
      navigate('/verify-phone', { replace: true });
      return;
    }

    if (profileData.verificationStatus === 'approved') {
      navigate('/messages', { replace: true });
      return 'approved';
    }

    const nextStatus = profileData.verificationStatus || 'review';
    setStatus(nextStatus);
    return nextStatus;
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;

    const submitForReview = async () => {
      try {
        setLoading(true);
        const functions = getFunctions();
        const submitRegistrationReview = httpsCallable(
          functions,
          'verifyUserIdentityV2'
        );
        await submitRegistrationReview();
        if (!cancelled) await refreshStatus();
      } catch (error) {
        console.error('submitRegistrationReview error:', error);
        if (!cancelled) {
          toast.error(error?.message || 'Unable to submit registration for review.');
          await refreshStatus();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    submitForReview();

    return () => {
      cancelled = true;
    };
  }, [refreshStatus]);

  const handleSignOut = async () => {
    try {
      await auth.signOut();
    } catch (_) {}
    clearCurrentUserProfile();
    navigate('/login', { replace: true });
  };

  const handleCheckStatus = async () => {
    try {
      setLoading(true);
      const nextStatus = await refreshStatus();
      if (nextStatus === 'rejected') {
        toast.error('Registration was not approved.');
      } else if (nextStatus && nextStatus !== 'approved') {
        toast.info('Registration is still under review. Please check again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const isRejected = status === 'rejected';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-8">
      <div className="w-full max-w-md">
        <RegistrationProgress currentStep="review" className="mb-4" />
        <div className="bg-white rounded-xl p-6 shadow w-full text-center">
          <img
            src="/coldwire-title.png"
            onError={(event) => { event.currentTarget.src = '/icon.png'; }}
            alt="ColdWire"
            className="mx-auto mb-4 h-auto w-full max-w-xs object-contain"
          />
          <h1 className="text-xl font-semibold text-slate-900">
            {isRejected ? 'Registration Not Approved' : 'Registration Pending Review'}
          </h1>
          <div
            className={`mt-4 rounded-lg border px-4 py-3 text-sm leading-6 ${
              isRejected
                ? 'border-red-200 bg-red-50 text-red-700'
                : 'border-blue-100 bg-blue-50 text-slate-700'
            }`}
          >
            {isRejected ? (
              <>
                <p>
                  Your registration has been reviewed and was <strong>not approved</strong>.
                </p>
                <p className="mt-2">
                  {/* TODO: Replace this placeholder with the real support email before launch. */}
                  If you believe this decision was made in error, please contact us at [support email to be added later].
                </p>
              </>
            ) : (
              <p>
                Your registration is awaiting administrator approval. Please check back later. Reviews may take up to 24 hours.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={handleCheckStatus}
            disabled={loading}
            className="mt-5 min-h-[44px] w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white disabled:opacity-50"
          >
            {loading ? 'Checking...' : 'Check review status'}
          </button>
          <button
            type="button"
            onClick={handleSignOut}
            className="mt-3 min-h-[44px] w-full rounded-lg border border-slate-300 px-4 py-3 text-sm font-medium text-slate-700"
          >
            Sign out
          </button>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
}
